import { useAuth } from '@platform/auth-client';
import { DENSITIES, THEME_MODES, usePreferences, useTheme } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import { LocaleSwitch } from './LocaleSwitch.tsx';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import styles from './Shell.module.css';

type MenuItemProps = { readonly onSelect: () => void; readonly children: ReactNode };

const WORD_SEPARATOR = /\s+/u;

export const initialsOf = (name: string, email: string): string => {
  const words = name.trim().split(WORD_SEPARATOR).filter(Boolean);
  const source = words.length > 0 ? words.slice(0, 2).map((word) => word.charAt(0)) : [email.charAt(0)];
  return source.join('').toUpperCase();
};

const MenuItem = ({ onSelect, children }: MenuItemProps) => (
  <button type="button" role="menuitem" className={styles['menuItem']} onClick={onSelect}>
    {children}
  </button>
);

export const UserMenu = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { mode, setMode } = useTheme();
  const density = usePreferences((state) => state.density);
  const setDensity = usePreferences((state) => state.setDensity);
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);
  return (
    <div ref={root}>
      <button
        type="button"
        className={styles['avatarButton']}
        aria-label={t('shell.userMenu')}
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <span className={styles['avatar']} aria-hidden="true">
          {initialsOf(auth.user?.name ?? '', auth.user?.email ?? '')}
        </span>
      </button>
      {open ? (
        <div className={styles['menu']} role="menu">
          <span className={styles['menuMeta']}>{auth.user?.email ?? ''}</span>
          <span className={styles['menuMeta']}>{t('shell.language')}</span>
          <LocaleSwitch />
          <span className={styles['menuMeta']}>{t('shell.theme')}</span>
          {THEME_MODES.map((candidate) => (
            <MenuItem
              key={candidate}
              onSelect={() => {
                setMode(candidate);
              }}
            >
              {t(`shell.theme_${candidate}`)}
              {candidate === mode ? ' ✓' : ''}
            </MenuItem>
          ))}
          <span className={styles['menuMeta']}>{t('shell.density')}</span>
          {DENSITIES.map((candidate) => (
            <MenuItem
              key={candidate}
              onSelect={() => {
                setDensity(candidate);
              }}
            >
              <span className={styles['menuChoice']}>
                {t(`shell.density_${candidate}`)}
                {candidate === density ? ' ✓' : ''}
                <small className={styles['menuHint']}>{t(`shell.densityHint_${candidate}`)}</small>
              </span>
            </MenuItem>
          ))}
          <MenuItem
            onSelect={() => {
              void auth.logout();
            }}
          >
            {t('shell.logout')}
          </MenuItem>
        </div>
      ) : null}
    </div>
  );
};
